/// Catálogo comercial puro, compartilhado pela API e pela navegação web.
export interface CapabilityDependencies {
  requiresModules: readonly string[];
  requiresFeatures: readonly string[];
}

/// Uma parte vendável de um módulo. Existe para o plano poder incluir o módulo sem incluir tudo
/// dele — e por isso só vira feature o que faz sentido cobrar à parte, nunca um recorte técnico.
export interface FeatureDefinition extends CapabilityDependencies {
  key: string;
  displayName: string;
  description: string;
}

export interface ModuleDefinition extends CapabilityDependencies {
  key: string;
  displayName: string;
  description: string;
  /// `false` marca o que todo tenant tem por definição. Login, papéis, permissões e a auditoria de
  /// tudo isso não se vendem à parte: sem eles não há sistema onde vender o resto.
  sellable: boolean;
  features: readonly FeatureDefinition[];
}

export interface CapabilitySelection {
  moduleKeys: readonly string[];
  featureKeys: readonly string[];
}

export interface MissingCapabilityRequirement {
  capabilityKey: string;
  requiredKey: string;
  requiredKind: 'MODULE' | 'FEATURE';
}

export interface CapabilitySelectionValidation {
  valid: boolean;
  unknownModuleKeys: string[];
  unknownFeatureKeys: string[];
  missingRequirements: MissingCapabilityRequirement[];
}

function dependencies(input: CapabilityDependencies): CapabilityDependencies {
  return {
    requiresModules: Object.freeze([...input.requiresModules]),
    requiresFeatures: Object.freeze([...input.requiresFeatures]),
  };
}

const NO_DEPENDENCIES = dependencies({ requiresModules: [], requiresFeatures: [] });

function feature<const Key extends string>(
  key: Key,
  displayName: string,
  description: string,
  requirements: CapabilityDependencies,
): FeatureDefinition & { key: Key } {
  return Object.freeze({ key, displayName, description, ...dependencies(requirements) });
}

function module<const Key extends string, const Features extends readonly FeatureDefinition[]>(
  key: Key,
  displayName: string,
  description: string,
  sellable: boolean,
  features: Features,
  requirements: CapabilityDependencies,
): ModuleDefinition & { key: Key; features: Features } {
  return Object.freeze({
    key,
    displayName,
    description,
    sellable,
    features: Object.freeze(features),
    ...dependencies(requirements),
  });
}

export const moduleCatalog = Object.freeze([
  module(
    'access',
    'Pessoas e acessos',
    'Quem entra no sistema, com qual perfil e com quais permissões.',
    false,
    [],
    NO_DEPENDENCIES,
  ),
  module(
    'audit',
    'Auditoria',
    'O registro de quem fez o quê, e quando.',
    false,
    [],
    NO_DEPENDENCIES,
  ),
  module(
    'investment',
    'Investimento inicial',
    'Sócios, aportes e lançamentos do dinheiro que montou o negócio.',
    true,
    [
      feature(
        'investment.receipts',
        'Comprovantes',
        'Anexar comprovante por upload ou link, com verificação do arquivo.',
        NO_DEPENDENCIES,
      ),
      feature(
        'investment.slips',
        'Boletos',
        'Boleto por parcela, com leitura da linha digitável a partir do PDF do carnê.',
        NO_DEPENDENCIES,
      ),
    ],
    NO_DEPENDENCIES,
  ),
  module(
    'clients',
    'Clientes',
    'Cadastro próprio de clientes da empresa.',
    true,
    [],
    NO_DEPENDENCIES,
  ),
  module('services', 'Serviços', 'Catálogo dos serviços oferecidos.', true, [], NO_DEPENDENCIES),
  module('notifications', 'Comunicação', 'Modelos e envio de e-mail.', true, [], NO_DEPENDENCIES),
  module(
    'finance',
    'Financeiro',
    'Contas financeiras e as capacidades que acompanham o dinheiro da empresa.',
    true,
    [],
    NO_DEPENDENCIES,
  ),
  module(
    'scheduling',
    'Agendamento',
    'Agenda, profissionais, salas e bloqueios da operação.',
    true,
    [],
    dependencies({ requiresModules: ['clients', 'services'], requiresFeatures: [] }),
  ),
  module(
    'protocols',
    'Protocolos',
    'O processo de cada serviço, etapa a etapa.',
    true,
    [
      feature(
        'protocols.materials',
        'Materiais dos protocolos',
        'Consumo previsto de produtos em cada etapa do protocolo.',
        dependencies({ requiresModules: ['inventory'], requiresFeatures: [] }),
      ),
      /// Aplicar exige agenda porque a aplicação aponta um atendimento: é dependência obrigatória,
      /// não degradação. Sem a feature, o módulo segue entregando o processo (ADR 0029).
      feature(
        'protocols.applications',
        'Aplicação do protocolo',
        'Qual protocolo será usado no atendimento e qual foi efetivamente aplicado.',
        dependencies({ requiresModules: ['scheduling'], requiresFeatures: [] }),
      ),
    ],
    dependencies({ requiresModules: ['services'], requiresFeatures: [] }),
  ),
  module(
    'inventory',
    'Estoque',
    'Produtos, locais, saldos, movimentações e alertas de reposição.',
    true,
    [
      feature(
        'inventory.materials-usage',
        'Consumo de materiais',
        'O que saiu do estoque em cada atendimento, contra o previsto quando houver protocolo.',
        dependencies({ requiresModules: ['scheduling'], requiresFeatures: [] }),
      ),
    ],
    NO_DEPENDENCIES,
  ),
  /// Sem dependência obrigatória de propósito: contratar serviço é compra sem mercadoria, e exigir
  /// Estoque para cadastrar um fornecedor transformaria uma dependência opcional em obrigatória.
  /// A oferta de produto é o que degrada quando Estoque está fora (ADR 0028).
  module(
    'purchasing',
    'Compras',
    'Fornecedores, ofertas comerciais, pedidos e recebimentos.',
    true,
    [],
    NO_DEPENDENCIES,
  ),
  /// O dado de saúde da cliente é outra área, para outro público, com permissões próprias — por
  /// isso não é feature de Clientes. Serviços, Agenda, Protocolos e Consumo são provedores
  /// opcionais: sem eles, fichas e Quadro de saúde funcionam e o resto mostra menos (ADR 0038).
  module(
    'records',
    'Prontuário',
    'Fichas de saúde da cliente, modelos de ficha e o Quadro de saúde.',
    true,
    [],
    dependencies({ requiresModules: ['clients'], requiresFeatures: [] }),
  ),
  /// Serviços e Estoque são fontes opcionais do que se vende: sem eles, a oferta só não encontra
  /// itens daquela natureza (ADR 0046).
  module(
    'sales',
    'Vendas',
    'Pacotes, promoções, vendas, repartição do preço e pagamentos.',
    true,
    [
      feature(
        'sales.commissions',
        'Comissões',
        'Comissão por item vendido, divisão entre profissionais, vales e apuração mensal.',
        dependencies({ requiresModules: ['scheduling'], requiresFeatures: [] }),
      ),
    ],
    NO_DEPENDENCIES,
  ),
  module(
    'prepaid-balances',
    'Saldos pré-pagos',
    'Sessões e produtos comprados antecipadamente, com regras de liberação e uso.',
    true,
    [],
    dependencies({ requiresModules: ['sales', 'clients'], requiresFeatures: [] }),
  ),
  module(
    'online-payments',
    'Cobrança online',
    'Pix e link de pagamento nas vendas, confirmados sozinhos pelo provedor.',
    true,
    [],
    dependencies({ requiresModules: ['sales'], requiresFeatures: [] }),
  ),
  /// Outro público e outra promessa que o Financeiro: sócios, gestor e contador olham se a empresa
  /// dá lucro e para onde vai o dinheiro. Exige o Financeiro, porque o que se classifica é o
  /// extrato e os compromissos dele; o Financeiro não depende deste módulo (ADR 0061).
  module(
    'accounting',
    'Contabilidade gerencial',
    'Plano de contas e classificação de receitas e despesas para o DRE e o fluxo de caixa.',
    true,
    [],
    dependencies({ requiresModules: ['finance'], requiresFeatures: [] }),
  ),
]);

export type ModuleKey = (typeof moduleCatalog)[number]['key'];
export type FeatureKey = (typeof moduleCatalog)[number]['features'][number]['key'];

/// As features de **um** módulo só. É o que permite ao catálogo de permissões recusar, em
/// compilação, uma feature de outro dono: sem isto, `finance` aceitaria `investment.slips` e a
/// checagem só existiria no teste, depois do erro já escrito.
///
/// Módulo sem feature resolve para `never`, e aí a permissão só aceita `null` — que é exatamente o
/// contrato de um módulo vendido em bloco único.
export type FeatureKeyOf<Owner extends ModuleKey> = Extract<
  (typeof moduleCatalog)[number],
  { key: Owner }
>['features'][number]['key'];

/// Valida o grafo inteiro antes de ele ser usado. A aresta implícita feature → módulo dono entra na
/// busca de ciclo: um módulo exigir uma feature própria também seria uma capacidade impossível de
/// contratar por fechamento.
export function validateModuleCatalog(catalog: readonly ModuleDefinition[]): void {
  const modules = new Map<string, ModuleDefinition>();
  const features = new Map<string, { definition: FeatureDefinition; ownerKey: string }>();

  for (const item of catalog) {
    if (modules.has(item.key)) throw catalogError(`Módulo repetido: "${item.key}".`);
    modules.set(item.key, item);
    for (const featureItem of item.features) {
      if (features.has(featureItem.key) || modules.has(featureItem.key)) {
        throw catalogError(`Capacidade repetida: "${featureItem.key}".`);
      }
      if (!featureItem.key.startsWith(`${item.key}.`)) {
        throw catalogError(`A feature "${featureItem.key}" não pertence ao módulo "${item.key}".`);
      }
      features.set(featureItem.key, { definition: featureItem, ownerKey: item.key });
    }
  }

  for (const key of modules.keys()) {
    if (features.has(key)) throw catalogError(`Capacidade repetida: "${key}".`);
  }

  const edges = new Map<string, string[]>();
  for (const item of catalog) {
    edges.set(item.key, requirementKeys(item, modules, features));
    for (const featureItem of item.features) {
      edges.set(featureItem.key, [item.key, ...requirementKeys(featureItem, modules, features)]);
    }
  }

  const visiting = new Set<string>();
  const visited = new Set<string>();
  const path: string[] = [];
  const visit = (key: string): void => {
    if (visiting.has(key)) {
      const start = path.indexOf(key);
      throw catalogError(`Ciclo de capacidades: ${[...path.slice(start), key].join(' → ')}.`);
    }
    if (visited.has(key)) return;
    visiting.add(key);
    path.push(key);
    for (const required of edges.get(key)!) visit(required);
    path.pop();
    visiting.delete(key);
    visited.add(key);
  };
  for (const key of edges.keys()) visit(key);
}

function requirementKeys(
  item: CapabilityDependencies & { key: string },
  modules: ReadonlyMap<string, ModuleDefinition>,
  features: ReadonlyMap<string, unknown>,
): string[] {
  assertUniqueRequirements(item.key, 'módulo', item.requiresModules);
  assertUniqueRequirements(item.key, 'feature', item.requiresFeatures);
  for (const required of item.requiresModules) {
    if (!modules.has(required)) {
      throw catalogError(`"${item.key}" exige o módulo inexistente "${required}".`);
    }
  }
  for (const required of item.requiresFeatures) {
    if (!features.has(required)) {
      throw catalogError(`"${item.key}" exige a feature inexistente "${required}".`);
    }
  }
  return [...item.requiresModules, ...item.requiresFeatures];
}

function assertUniqueRequirements(ownerKey: string, kind: string, keys: readonly string[]): void {
  if (new Set(keys).size !== keys.length) {
    throw catalogError(`"${ownerKey}" repete uma dependência de ${kind}.`);
  }
}

function catalogError(message: string): Error {
  return new Error(`Catálogo de módulos inválido. ${message}`);
}

validateModuleCatalog(moduleCatalog);

const byKey = new Map<string, ModuleDefinition>(moduleCatalog.map((item) => [item.key, item]));
const featureByKey = new Map<string, FeatureDefinition>(
  moduleCatalog.flatMap((item) => item.features.map((entry) => [entry.key, entry] as const)),
);

export function findModule(key: string): ModuleDefinition | undefined {
  return byKey.get(key);
}

export function findFeature(key: string): FeatureDefinition | undefined {
  return featureByKey.get(key);
}

/// Retorna todas as lacunas do conjunto, inclusive as transitivas: como cada capacidade escolhida
/// também é inspecionada, fechar as arestas diretas fecha o grafo inteiro.
export function validateCapabilitySelection(
  selection: CapabilitySelection,
  catalog: readonly ModuleDefinition[] = moduleCatalog,
): CapabilitySelectionValidation {
  validateModuleCatalog(catalog);
  const modules = new Map(catalog.map((item) => [item.key, item]));
  const features = new Map(
    catalog.flatMap((item) =>
      item.features.map((entry) => [entry.key, { definition: entry, owner: item }] as const),
    ),
  );
  const selectedModules = new Set(selection.moduleKeys);
  const selectedFeatures = new Set(selection.featureKeys);
  const unknownModuleKeys = [...selectedModules].filter((key) => !modules.has(key)).sort();
  const unknownFeatureKeys = [...selectedFeatures].filter((key) => !features.has(key)).sort();
  const missingRequirements: MissingCapabilityRequirement[] = [];

  const requireModule = (capabilityKey: string, requiredKey: string): void => {
    const required = modules.get(requiredKey)!;
    if (required.sellable && !selectedModules.has(requiredKey)) {
      missingRequirements.push({ capabilityKey, requiredKey, requiredKind: 'MODULE' });
    }
  };
  const inspect = (item: CapabilityDependencies & { key: string }): void => {
    for (const requiredKey of item.requiresModules) requireModule(item.key, requiredKey);
    for (const requiredKey of item.requiresFeatures) {
      if (!selectedFeatures.has(requiredKey)) {
        missingRequirements.push({
          capabilityKey: item.key,
          requiredKey,
          requiredKind: 'FEATURE',
        });
      }
    }
  };

  for (const item of catalog) {
    if (!item.sellable || selectedModules.has(item.key)) inspect(item);
  }
  for (const key of selectedFeatures) {
    const selected = features.get(key);
    if (!selected) continue;
    requireModule(key, selected.owner.key);
    inspect(selected.definition);
  }

  missingRequirements.sort((left, right) =>
    `${left.capabilityKey}:${left.requiredKey}`.localeCompare(
      `${right.capabilityKey}:${right.requiredKey}`,
    ),
  );
  return {
    valid:
      unknownModuleKeys.length === 0 &&
      unknownFeatureKeys.length === 0 &&
      missingRequirements.length === 0,
    unknownModuleKeys,
    unknownFeatureKeys,
    missingRequirements,
  };
}

/// Módulos que um plano pode escolher. O núcleo fica de fora porque não é escolha de ninguém.
export function sellableModules(): readonly ModuleDefinition[] {
  return moduleCatalog.filter((item) => item.sellable);
}

/// Os módulos que todo tenant tem, contratados ou não.
export function coreModuleKeys(): string[] {
  return moduleCatalog.filter((item) => !item.sellable).map((item) => item.key);
}
